import { type KeyboardEvent, type ReactNode, useEffect, useId, useRef, useState } from "react";
import { matchScore } from "../lib/match";
import { Popover } from "./Popover";

export interface ComboOption<T> {
  value: T;
  label: string;
  /** 찾기에 함께 쓰는 글 (아이디 등) */
  keywords?: string;
  hint?: string;
  icon?: ReactNode;
}

/**
 * 찾아서 고르는 목록 (담당자 등). 단추를 누르면 찾기 칸과 목록이 뜨고, ↑↓·Enter·Esc로 고른다.
 * 한글 초성으로도 찾는다.
 */
export function Combobox<T>({
  label,
  value,
  options,
  onChange,
  disabled,
  renderValue,
  placeholder = "찾기",
}: {
  label: string;
  value: T;
  options: ComboOption<T>[];
  onChange: (v: T) => void;
  disabled?: boolean;
  renderValue: (selected: ComboOption<T> | undefined) => ReactNode;
  placeholder?: string;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const listId = useId();
  const listRef = useRef<HTMLUListElement>(null);
  const selected = options.find((o) => Object.is(o.value, value));
  const shown = options.filter((o) => !query.trim() || matchScore(`${o.label} ${o.keywords ?? ""}`, query) > 0);

  useEffect(() => {
    if (!anchor) return;
    setQuery("");
    setActive(Math.max(0, options.findIndex((o) => Object.is(o.value, value))));
  }, [anchor]); // eslint-disable-line react-hooks/exhaustive-deps -- 열 때만
  useEffect(() => {
    listRef.current?.children[active]?.scrollIntoView({ block: "nearest" });
  }, [active]);

  const pick = (o: ComboOption<T>) => {
    setAnchor(null);
    if (!Object.is(o.value, value)) onChange(o.value);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.keyCode === 229) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (shown.length ? (i + (e.key === "ArrowDown" ? 1 : -1) + shown.length) % shown.length : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = shown[active];
      if (o) pick(o);
    }
  };

  return (
    <>
      <button type="button" className="combo-trigger" disabled={disabled} aria-haspopup="listbox" aria-expanded={!!anchor} aria-label={`${label}: ${selected?.label ?? "없음"}`} onClick={(e) => setAnchor(anchor ? null : e.currentTarget)}>
        {renderValue(selected)}
        <svg viewBox="0 0 24 24" aria-hidden="true" className="caret">
          <path d="M7 10l5 5 5-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      <Popover anchor={anchor} open={!!anchor} onClose={() => setAnchor(null)} className="combo-pop" role="presentation">
        <input
          className="input input-sm"
          role="combobox"
          aria-label={`${label} 찾기`}
          aria-expanded="true"
          aria-controls={listId}
          aria-activedescendant={shown[active] ? `${listId}-${active}` : undefined}
          value={query}
          placeholder={placeholder}
          onChange={(e) => {
            setQuery(e.target.value);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
          autoFocus
          autoComplete="off"
        />
        <ul id={listId} ref={listRef} role="listbox" aria-label={label} className="combo-list">
          {shown.length === 0 && <li className="combo-empty">맞는 항목이 없습니다</li>}
          {shown.map((o, i) => (
            <li
              key={String(o.value)}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={Object.is(o.value, value)}
              className={`combo-option${i === active ? " on" : ""}`}
              onPointerMove={() => i !== active && setActive(i)}
              onClick={() => pick(o)}
            >
              {o.icon}
              <span className="co-label">{o.label}</span>
              {o.hint && <span className="co-hint">{o.hint}</span>}
              {Object.is(o.value, value) && (
                <svg viewBox="0 0 24 24" aria-hidden="true" className="co-check">
                  <path d="M5 12l5 5L20 7" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </li>
          ))}
        </ul>
      </Popover>
    </>
  );
}
